/**
 * Words for the codes the fleet route serves (SW-8879). Reason codes follow
 * the backend's `readiness_events.REASONS`; the customer and allocation words
 * mirror the config schema's labels for the values the page meets most, and
 * fall back to the raw value so a new one still reads.
 */
import { TRACTOR_TZ } from "./time";
import { FleetNowEntry } from "./types";

export { TRACTOR_TZ };

const REASON_LABELS: Record<string, string> = {
  not_ready: "Not ready",
  pmi: "PMI",
  maintenance: "Maintenance",
  v_and_v: "V&V",
  offline: "Offline",
  fault: "Fault",
  unset: "No mark",
};

export const reasonLabel = (code: string): string =>
  REASON_LABELS[code] ?? code;

/**
 * The reason worth saying first. `not_ready` is the mark itself; when a
 * blocker sits beside it the blocker is the news ("PMI"), so it wins.
 */
export function primaryReason(reasons: string[]): string | null {
  if (!reasons.length) return null;
  return reasons.find((r) => r !== "not_ready") ?? reasons[0];
}

const CUSTOMER_LABELS: Record<string, string> = {
  aerovect: "AeroVect",
  delta: "Delta",
};

export const customerLabel = (customer: string): string =>
  CUSTOMER_LABELS[customer] ??
  customer.charAt(0).toUpperCase() + customer.slice(1);

const ALLOCATION_LABELS: Record<string, string> = {
  OPS: "Ops",
  ENG: "Engineering",
  ENG_OPS_SHARED: "Eng/Ops shared",
  HW_SERVICING: "HW servicing",
  CUSTOMER_PMI: "Customer PMI",
  V_AND_V: "V&V",
  OFFLINE: "Offline",
  UNASSIGNED: "Unassigned",
};

export const allocationLabel = (allocation: string): string =>
  ALLOCATION_LABELS[allocation] ?? allocation;

export type NowTone = "ok" | "bad" | "muted";

/** What a tractor's `now` says, in words and a tone. */
export function describeNow(now: FleetNowEntry | null): {
  text: string;
  tone: NowTone;
} {
  if (!now || now.value === "UNSET") {
    return { text: "No readiness mark", tone: "muted" };
  }
  if (now.value === "READY") return { text: "Production ready", tone: "ok" };
  const reason = primaryReason(now.reasons);
  return {
    text: reason ? `Not ready · ${reasonLabel(reason)}` : "Not ready",
    tone: "bad",
  };
}

/** "since Oct 14, 2:30 PM", in the tractor's zone. */
export function sinceText(iso: string | null): string | null {
  if (!iso) return null;
  try {
    return `since ${new Date(iso).toLocaleString("en-US", {
      timeZone: TRACTOR_TZ,
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    })}`;
  } catch {
    return `since ${iso}`;
  }
}

/** "Mon 20" for a column header, from a local day. */
export function dayHeader(localDay: string): string {
  return new Date(`${localDay}T12:00:00`).toLocaleDateString("en-US", {
    weekday: "short",
    day: "numeric",
  });
}

/** "Fri Oct 17 – Thu Oct 23, 2026" or one day. */
export function spanTitle(from: string, to: string): string {
  const fmt = (iso: string, year: boolean) =>
    new Date(`${iso}T12:00:00`).toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      ...(year ? { year: "numeric" } : {}),
    });
  if (from === to) return fmt(to, true);
  return `${fmt(from, false)} – ${fmt(to, true)}`;
}

/** "11:20 EDT" for the right-now strip. */
export function clockText(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString("en-US", {
      timeZone: TRACTOR_TZ,
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZoneName: "short",
    });
  } catch {
    return iso;
  }
}
